import { apiFetch } from '@/lib/http';
import React, { useState, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Search, Plus, Store } from 'lucide-react';
import type { SnapshotMutation } from '@/types/snapshot-edit-types';

interface AddStoreManualModalProps {
    isOpen: boolean;
    onClose: () => void;
    retailerId: number;
    existingStoreIds: Set<number>;
    usedFormatIds?: Set<number>;
    virtualPackId: number;
    onConfirm: (mutation: SnapshotMutation) => void;
}

export function AddStoreManualModal({
    isOpen,
    onClose,
    retailerId,
    existingStoreIds,
    usedFormatIds,
    virtualPackId,
    onConfirm,
}: AddStoreManualModalProps) {
    // State
    const [step, setStep] = useState<'store' | 'formats'>('store');
    const [isLoadingStores, setIsLoadingStores] = useState(false);
    const [isLoadingFormats, setIsLoadingFormats] = useState(false);
    const [allRetailerStores, setAllRetailerStores] = useState<any[]>([]);
    const [availableFormats, setAvailableFormats] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStore, setSelectedStore] = useState<any | null>(null);
    const [selectedFormats, setSelectedFormats] = useState<Map<number, number>>(new Map()); // formatId -> quantity

    // Fetch stores
    useEffect(() => {
        if (isOpen && retailerId) {
            const fetchStores = async () => {
                setIsLoadingStores(true);
                try {
                    const response = await fetch(`/api/retailers/${retailerId}/stores?all=true`);
                    if (response.ok) {
                        const data = await response.json();
                        setAllRetailerStores(data);
                    }
                } finally {
                    setIsLoadingStores(false);
                }
            };
            fetchStores();
        }
    }, [isOpen, retailerId]);

    // Fetch formats
    useEffect(() => {
        if (isOpen && retailerId) {
            const fetchFormats = async () => {
                setIsLoadingFormats(true);
                try {
                    const response = await apiFetch(`/api/media-formats?retailerId=${retailerId}`);
                    if (response.ok) {
                        const data = await response.json();
                        setAvailableFormats(data);
                    }
                } finally {
                    setIsLoadingFormats(false);
                }
            };
            fetchFormats();
        }
    }, [isOpen, retailerId]);

    // Reset on open
    useEffect(() => {
        if (isOpen) {
            setStep('store');
            setSelectedStore(null);
            setSelectedFormats(new Map());
            setSearchQuery('');
        }
    }, [isOpen]);

    const filteredStores = useMemo(() => {
        return allRetailerStores.filter(s =>
            !existingStoreIds.has(s.id) &&
            (s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                s.retailer_identifier?.toLowerCase().includes(searchQuery.toLowerCase()))
        ).slice(0, 50);
    }, [allRetailerStores, existingStoreIds, searchQuery]);

    // Sort formats: Used ones first
    const sortedFormats = useMemo(() => {
        if (!usedFormatIds || usedFormatIds.size === 0) return availableFormats;
        return [...availableFormats].sort((a, b) => {
            const aUsed = usedFormatIds.has(a.id);
            const bUsed = usedFormatIds.has(b.id);
            if (aUsed && !bUsed) return -1;
            if (!aUsed && bUsed) return 1;
            return 0;
        });
    }, [availableFormats, usedFormatIds]);

    const handleFormatToggle = (formatId: number) => {
        const next = new Map(selectedFormats);
        if (next.has(formatId)) {
            next.delete(formatId);
        } else {
            next.set(formatId, 1);
        }
        setSelectedFormats(next);
    };

    const handleQuantityChange = (formatId: number, qty: number) => {
        if (qty < 1) return;
        const next = new Map(selectedFormats);
        next.set(formatId, qty);
        setSelectedFormats(next);
    };

    const handleConfirm = () => {
        if (!selectedStore || selectedFormats.size === 0) return;

        const formats = Array.from(selectedFormats.entries()).map(([formatId, quantity]) => {
            const format = availableFormats.find(f => f.id === formatId);
            return {
                packId: virtualPackId,
                mediaFormatId: formatId,
                mediaFormatName: format?.name || 'Unknown Format',
                operatorId: 0,
                operatorName: 'Unassigned',
                quantity,
            };
        });

        onConfirm({
            type: 'ADD_STORE',
            storeId: selectedStore.id,
            storeName: selectedStore.name,
            storeCode: selectedStore.retailer_identifier,
            storeAddress: selectedStore.address,
            town: selectedStore.town,
            formats,
        });
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>
                        {step === 'store' ? 'Select Store' : `Add Formats to ${selectedStore?.name}`}
                    </DialogTitle>
                </DialogHeader>

                {step === 'store' ? (
                    <div className="flex-1 min-h-0 flex flex-col gap-4">
                        <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search stores..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-8"
                            />
                        </div>
                        <ScrollArea className="flex-1 border rounded-md h-[400px]">
                            <div className="p-2 space-y-1">
                                {isLoadingStores ? (
                                    <div className="flex justify-center p-4">
                                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                    </div>
                                ) : filteredStores.length === 0 ? (
                                    <div className="text-center p-4 text-muted-foreground text-sm">
                                        No matching stores found
                                    </div>
                                ) : (
                                    filteredStores.map(store => (
                                        <Button
                                            key={store.id}
                                            variant="ghost"
                                            className="w-full justify-start font-normal h-auto py-3"
                                            onClick={() => {
                                                setSelectedStore(store);
                                                setStep('formats');
                                            }}
                                        >
                                            <Store className="mr-2 h-4 w-4 text-muted-foreground" />
                                            <div className="flex flex-col items-start gap-1">
                                                <span className="font-medium">{store.name}</span>
                                                {store.retailer_identifier && (
                                                    <span className="text-xs text-muted-foreground">
                                                        #{store.retailer_identifier}
                                                    </span>
                                                )}
                                            </div>
                                        </Button>
                                    ))
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                ) : (
                    <div className="flex-1 min-h-0 flex flex-col gap-4">
                        <ScrollArea className="flex-1 border rounded-md h-[400px]">
                            <div className="p-4 space-y-4">
                                {isLoadingFormats ? (
                                    <div className="flex justify-center p-4">
                                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                    </div>
                                ) : sortedFormats.length === 0 ? (
                                    <div className="text-center p-4 text-muted-foreground">
                                        No formats available for this retailer
                                    </div>
                                ) : (
                                    sortedFormats.map(format => {
                                        const isSelected = selectedFormats.has(format.id);
                                        const isUsedInCampaign = usedFormatIds?.has(format.id);
                                        return (
                                            <div key={format.id} className={`flex items-center justify-between p-2 border rounded-md hover:bg-muted/50 ${isUsedInCampaign ? 'bg-blue-50/50' : ''}`}>
                                                <div className="flex items-center gap-3">
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onCheckedChange={() => handleFormatToggle(format.id)}
                                                        id={`format-${format.id}`}
                                                    />
                                                    <div className="flex flex-col">
                                                        <Label htmlFor={`format-${format.id}`} className="cursor-pointer font-medium">
                                                            {format.name}
                                                        </Label>
                                                        {isUsedInCampaign && (
                                                            <span className="text-[10px] text-blue-600 font-medium">Used in campaign</span>
                                                        )}
                                                    </div>
                                                </div>
                                                {isSelected && (
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-muted-foreground">Qty</span>
                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            className="w-20 h-8"
                                                            value={selectedFormats.get(format.id)}
                                                            onChange={(e) => handleQuantityChange(format.id, parseInt(e.target.value) || 1)}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                )}

                <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={() => {
                        if (step === 'formats') setStep('store');
                        else onClose();
                    }}>
                        {step === 'formats' ? 'Back' : 'Cancel'}
                    </Button>
                    {step === 'formats' && (
                        <Button
                            onClick={handleConfirm}
                            disabled={!selectedStore || selectedFormats.size === 0}
                        >
                            <Plus className="mr-2 h-4 w-4" />
                            Add Store
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default AddStoreManualModal;
